import React, { useState } from 'react';
import Alert from '@mui/material/Alert';
import MainLayout from '../../layouts/MainLayout';
import apiClient from '../../api/apiClient';
import { Spinner } from '../../components/Progress';
import SearchableStartupDropdown from '../../components/SearchableStartupDropdown';
import styles from './VideoStyles.module.css';

const client_transcription = apiClient('transcription');
const client_sentiment = apiClient('sentiment');
const client_emotions = apiClient('emotions');

const handleGenericDownload = async ({
  client,
  startupName,
  endpoint,
  filename,
  notFoundMessage,
  serverErrorMessagePrefix,
}) => {
  try {
    const response = await client.get(
      endpoint.replace('{startup}', startupName),
      {
        responseType: 'blob',
      }
    );

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    return { success: true };
  } catch (error) {
    if (error.response?.status === 404) {
      return { success: false, message: notFoundMessage };
    } else if (error.response?.status === 500) {
      return {
        success: false,
        message: `${serverErrorMessagePrefix}: ${error.response.data.message}`,
      };
    }

    console.error('Download failed:', error);
    return { success: false, message: 'An unexpected error occurred.' };
  }
};

export const handleDownloadEmotions = (startupName) =>
  handleGenericDownload({
    client: client_emotions,
    startupName,
    endpoint: '/download/{startup}',
    filename: `${startupName}_emotions_analysis.zip`,
    notFoundMessage: 'Emotions Analysis not found for this startup.',
    serverErrorMessagePrefix: 'Error during creation of emotions analysis',
  });

export const handleDownloadTranscription = (startupName) =>
  handleGenericDownload({
    client: client_transcription,
    startupName,
    endpoint: '/download/{startup}',
    filename: `${startupName}_transcription.docx`,
    notFoundMessage: 'Transcription not found for this startup.',
    serverErrorMessagePrefix: 'Error during creation of transcript',
  });

export const handleDownloadSentimentCoding = (startupName) =>
  handleGenericDownload({
    client: client_sentiment,
    startupName,
    endpoint: '/download/{startup}',
    filename: `${startupName}_sentiment_analysis.zip`,
    notFoundMessage: 'Sentiment coding not found for this startup.',
    serverErrorMessagePrefix: 'Error during creation of sentitment codings',
  });

function DownloadPage() {
  const [startupName, setStartupName] = useState('');
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);

  const handleButtonClick = async (downloadFunc) => {
    if (!startupName) {
      setError('Please select a startup name first.');
      return;
    }
    setDownloading(true);
    const result = await downloadFunc(startupName);
    setDownloading(false);
    if (!result.success) setError(result.message);
    else setError('');
  };

  return (
    <MainLayout>
      <div style={{ margin: '20px' }}>
        <h1>Download Data</h1>
        <div className={styles.filterBox}>
          <div className={styles.filterRow}>
            <label className={styles.filterLabel}>Startup Name:</label>
            <div style={{ flex: 1 }}>
              <SearchableStartupDropdown
                selectedStartup={startupName}
                setSelectedStartup={setStartupName}
              />
            </div>
          </div>
          <div className={styles.checkboxRow}>
            <button
              onClick={() => handleButtonClick(handleDownloadTranscription)}
              disabled={downloading}
            >
              Download Transcription
            </button>
            <button
              onClick={() => handleButtonClick(handleDownloadEmotions)}
              disabled={downloading}
            >
              Download Emotions
            </button>
            <button
              onClick={() => handleButtonClick(handleDownloadSentimentCoding)}
              disabled={downloading}
            >
              Download Sentiment Analysis
            </button>
          </div>
        </div>
        {downloading && (
          <p>
            Downloading... <Spinner />
          </p>
        )}
        {error && <Alert severity="error">{error}</Alert>}
      </div>
    </MainLayout>
  );
}

export default DownloadPage;
